import React, { useEffect, useMemo, useState } from "react";
import StepForm from "./steps/StepForm.jsx";
import StepReview from "./steps/StepReview.jsx";
import {
  getApplication,
  submitApplication,
} from "../../api/borrowerPortal/applications";

/* ---------------- Inline Stepper (optional internal) ---------------- */
function CheckIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      {...props}
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function InlineStepper({ currentStep = 1, labels = [] }) {
  return (
    <div className="w-full flex justify-center">
      <ol className="flex items-center w-full max-w-3xl">
        {labels.map((label, idx) => {
          const stepNumber = idx + 1;
          const isDone = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const isLast = stepNumber === labels.length;

          return (
            <li key={label} className="flex items-center w-full">
              <div className="flex flex-col items-center">
                <div
                  className={[
                    "flex items-center justify-center w-9 h-9 rounded-full border-2",
                    isDone
                      ? "bg-sky-500 border-sky-500 text-white"
                      : isCurrent
                        ? "bg-white border-sky-400 text-sky-600 shadow-sm"
                        : "bg-white border-slate-300 text-slate-500",
                  ].join(" ")}
                >
                  {isDone ? <CheckIcon /> : <span className="font-semibold">{stepNumber}</span>}
                </div>
                <div
                  className={[
                    "mt-2 text-xs md:text-sm font-medium",
                    isCurrent ? "text-sky-700" : "text-slate-600",
                  ].join(" ")}
                >
                  {`Step ${stepNumber}: ${label}`}
                </div>
              </div>

              {!isLast && (
                <div className="flex-1 mx-3">
                  <div className="h-1 bg-slate-200 rounded-full relative overflow-hidden">
                    <div
                      className={[
                        "absolute left-0 top-0 h-1 rounded-full",
                        isDone || isCurrent ? "bg-sky-400 w-full" : "bg-slate-200 w-0",
                      ].join(" ")}
                    />
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ---------------- Borrower ↔ Product compatibility ---------------- */
const COMPAT_MATRIX = {
  individual: ["BTL", "CBTL", "IF", "ASSET"],
  limited: ["BTL", "CBTL", "IF", "ASSET"],
  partnership: ["BTL", "CBTL", "IF"],
  pension: ["BTL", "CBTL", "IF", "ASSET"],
  llp: ["BTL", "CBTL", "IF", "ASSET"],
};

const SUPPORTED_TYPES = ["BTL", "CBTL", "IF", "ASSET"];

function safeJsonParse(v) {
  if (!v) return null;
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
}

export default function Wizard({
  borrowerType, // "individual" | "limited" | "partnership" | "pension"
  productType: lockedPt, // provided by NewApplication (from shortlist)
  productId, // optional (kept for payload only)
  loanPurpose, // loan purpose from product (Purchase, Remortgage, Product Transfer)
  applicationName: applicationNameProp, // ✅ NEW: pass existing Applications doc name if editing
  initialPayload: initialPayloadProp, // ✅ NEW: pass initial payload to restore form data
  onBack,
  onSubmit, // optional callback after final submit
  showStepper = false,
  skipReviewStep = false, // ✅ NEW: if true, skip Wizard's review step and call onSubmit when form completes
  isSubmitting = false,
}) {
  const lockedProductType = lockedPt ? String(lockedPt).toUpperCase() : null;

  // If product locked → start at 2 (Form)
  const [step, setStep] = useState(lockedProductType ? 2 : 1);
  const [productType, setProductType] = useState(lockedProductType);

  // ✅ Backend state
  const [applicationName, setApplicationName] = useState(applicationNameProp || null);
  const [initialPayload, setInitialPayload] = useState(initialPayloadProp || null); // Initialize with prop if provided
  const [fullPayload, setFullPayload] = useState(initialPayloadProp || null); // Initialize with prop if provided

  const [formData, setFormData] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState("");

  useEffect(() => {
    if (lockedProductType) {
      setProductType(lockedProductType);
      setStep(2);
    }
  }, [lockedProductType]);

  const isCompatible = useMemo(() => {
    if (!borrowerType || !productType) return false;
    const allowed = COMPAT_MATRIX[borrowerType] || [];
    return allowed.includes(productType);
  }, [borrowerType, productType]);

  useEffect(() => {
    if (productType && !SUPPORTED_TYPES.includes(productType)) {
      console.error("Unsupported product type in Wizard:", productType);
    }
  }, [productType]);

  // ✅ BOOT: load existing Application ONLY - never create automatically
  useEffect(() => {
    let alive = true;

    (async () => {
      setBootLoading(true);
      setBootError("");

      console.log("Wizard boot - productId:", productId, "borrowerType:", borrowerType, "productType:", lockedProductType || productType);

      try {
        // Keep in sync if parent passes prop later
        const incomingName = applicationNameProp || null;
        const incomingPayload = initialPayloadProp || null;

        // If initialPayload is provided as prop, use it (for going back from review)
        // This preserves form data when navigating back from review step
        if (incomingPayload) {
          setInitialPayload(incomingPayload);
          setFullPayload(incomingPayload);
          setBootLoading(false);
          return;
        }

        // If we already have a doc name, use it
        const nameToUse = incomingName || applicationName;

        if (nameToUse) {
          const doc = await getApplication(nameToUse);
          if (!alive) return;

          setApplicationName(doc?.name || nameToUse);

          const parsed = safeJsonParse(doc?.application_payload);
          setInitialPayload(parsed || null);
          setFullPayload(parsed || null);

          // If doc already contains productType, prefer locked → else keep existing
          if (!lockedProductType && doc?.product_type) {
            setProductType(String(doc.product_type).toUpperCase());
          }
        } else {
          // ✅ No application name and no initial payload?
          // Just set empty state - application will be created on final submit
          setInitialPayload(null);
          setFullPayload(null);
          setApplicationName(null);
        }
      } catch (e) {
        if (!alive) return;
        setBootError(e?.response?.data?.exception || e?.message || "Failed to load application");
      } finally {
        if (!alive) return;
        setBootLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationNameProp, initialPayloadProp, borrowerType, productId, lockedProductType]);

  if (lockedProductType && !isCompatible) {
    return (
      <div className="p-6 bg-white rounded-2xl shadow space-y-6">
        {showStepper && <InlineStepper currentStep={1} labels={["Product", "Form", "Review"]} />}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          The selected borrower type cannot apply for <strong>{lockedProductType}</strong>.
          Please go back and choose a different borrower type or product.
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (bootLoading) {
    return (
      <div className="p-6 bg-white rounded-2xl shadow space-y-4">
        {showStepper && <InlineStepper currentStep={step} labels={["Product", "Form", "Review"]} />}
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-slate-700">
          Loading application…
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="p-6 bg-white rounded-2xl shadow space-y-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
          {bootError}
        </div>
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-2xl shadow space-y-6">
      {showStepper && <InlineStepper currentStep={step} labels={["Product", "Form", "Review"]} />}

      {/* Step 2: Form */}
      {step === 2 && productType && (
        <StepForm
          borrowerType={borrowerType}
          productType={productType}
          productId={productId}
          applicationName={applicationName}   // ✅ NEW: for uploads + saving
          initialPayload={initialPayload}     // ✅ NEW: prefill when editing
          isSubmitting={isSubmitting}

          onBack={() => {
            if (lockedProductType) onBack?.();
            else setStep(1);
          }}

          // ✅ When form finishes (Next), store the data and store latest payload if form provides it
          onNext={async (data) => {
            setFormData(data);
            if (data?.payloadObj) setFullPayload(data.payloadObj);

            if (skipReviewStep) {
              onSubmit?.({
                ...data,
                borrowerType,
                productType,
                productId,
                formData: data,
                applicationName,
                fullPayload: data?.payloadObj || { formData: data },
              });
            } else {
              setStep(3);
            }
          }}
        />
      )}

      {/* Step 3: Review & Submit */}
      {step === 3 && (
        <StepReview
          borrowerType={borrowerType}
          productType={productType}
          productId={productId}
          formData={formData}
          fullPayload={fullPayload || initialPayload || { formData }}
          applicationName={applicationName}     // ✅ NEW

          onBack={() => setStep(2)}

          onConfirm={async () => {
            // ✅ Submit to backend + then bubble up
            await submitApplication(
              applicationName,
              fullPayload || initialPayload || { formData, borrowerType, productType, productId }
            );

            onSubmit?.({
              borrowerType,
              productType,
              productId,
              formData,
              applicationName,
              fullPayload: fullPayload || initialPayload || { formData, borrowerType, productType, productId },
              payloadObj: fullPayload || initialPayload, // Include payloadObj for data preservation
            });
          }}
        />
      )}
    </div>
  );
}
